import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import './SideNavigation.css';

interface SideNavigationProps {
    isOpen: boolean;
    onToggle: () => void;
}

const SideNavigation: React.FC<SideNavigationProps> = ({ isOpen, onToggle }) => {
    const { user, isAuthenticated, logout, hasPermission, isAdmin } = useAuth();
    const location = useLocation();

    const handleLogout = async () => {
        try {
            await logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    // Don't render if user is not authenticated
    if (!isAuthenticated) {
        return null;
    }

    const navigationItems = [
        {
            id: 'dashboard',
            label: 'Dashboard',
            icon: 'ri-dashboard-3-line',
            path: '/dashboard',
            active: location.pathname === '/dashboard'
        },
        {
            id: 'alerts',
            label: 'Alerts',
            icon: 'ri-alert-line',
            path: '/dashboard/alerts',
            active: location.pathname === '/dashboard/alerts'
        },
        {
            id: 'reports',
            label: 'Reports',
            icon: 'ri-file-chart-line',
            path: '/dashboard/reports',
            active: location.pathname === '/dashboard/reports'
        },
        {
            id: 'blogs',
            label: 'Blogs',
            icon: 'ri-article-line',
            path: '/dashboard/blogs',
            active: location.pathname === '/dashboard/blogs'
        },
        {
            id: 'profile',
            label: 'Profile',
            icon: 'ri-user-line',
            path: '/dashboard/profile',
            active: location.pathname === '/dashboard/profile'
        }
    ];

    const comingSoonItems = [
        {
            id: 'ai-insights',
            label: 'AI Insights',
            icon: 'ri-brain-line',
            path: '/dashboard/ai-insights',
            comingSoon: true
        }
    ];

    const specialItems = [
        {
            id: 'custom-monitoring',
            label: 'Request Custom Monitoring',
            icon: 'ri-settings-3-line',
            path: '/dashboard/custom-monitoring',
            special: true
        }
    ];

    // Admin items with permission checks
    const allAdminItems = [
        {
            id: 'admin-users',
            label: 'Users Management',
            icon: 'ri-user-settings-line',
            path: '/admin/users',
            active: location.pathname === '/admin/users',
            permission: 'users_view'
        },
        {
            id: 'admin-roles',
            label: 'Role Management',
            icon: 'ri-shield-user-line',
            path: '/admin/roles',
            active: location.pathname === '/admin/roles',
            permission: 'roles_view'
        },
        {
            id: 'admin-leads',
            label: 'Leads Management',
            icon: 'ri-user-add-line',
            path: '/admin/leads',
            active: location.pathname === '/admin/leads',
            permission: 'leads_view'
        },
        {
            id: 'admin-custom-monitoring',
            label: 'Custom Monitoring',
            icon: 'ri-eye-line',
            path: '/admin/custom-monitoring',
            active: location.pathname === '/admin/custom-monitoring',
            permission: 'custom_monitoring_view'
        },
        {
            id: 'admin-alerts',
            label: 'Alerts Management',
            icon: 'ri-alert-line',
            path: '/admin/alerts',
            active: location.pathname === '/admin/alerts',
            permission: 'alerts_view'
        },
        {
            id: 'admin-reports',
            label: 'Reports Management',
            icon: 'ri-file-chart-line',
            path: '/admin/reports',
            active: location.pathname === '/admin/reports',
            permission: 'reports_view'
        },
        {
            id: 'admin-blogs',
            label: 'Blogs Management',
            icon: 'ri-article-line',
            path: '/admin/blogs',
            active: location.pathname === '/admin/blogs',
            permission: 'blogs_view'
        }
    ];

    // Filter admin items based on user permissions
    const adminItems = allAdminItems.filter(item => 
        hasPermission(item.permission) || hasPermission('superadmin')
    );

    // Check if user has any admin permissions
    const hasAnyAdminPermission = isAdmin() || adminItems.length > 0;

    return (
        <>
            {/* Overlay for mobile */}
            {isOpen && (
                <div
                    className="sidenav-overlay"
                    onClick={onToggle}
                />
            )}

            {/* Side Navigation */}
            <nav className={`sidenav ${isOpen ? 'sidenav-open' : ''}`}>
                {/* Header */}
                <div className="sidenav-header">
                    <Link to="/dashboard" className="sidenav-brand">
                        <div className="sidenav-logo-icon">
                            <i className="ri-shield-star-fill"></i>
                        </div>
                        <span className="sidenav-logo-text">NUROVELI</span>
                    </Link>
                    <button
                        className="sidenav-toggle-btn"
                        onClick={onToggle}
                        aria-label="Toggle navigation"
                    >
                        <i className="ri-menu-fold-line"></i>
                    </button>
                </div>

                {/* User Info */}
                <div className="sidenav-user">
                    <div className="sidenav-user-avatar">
                        <i className="ri-user-fill"></i>
                    </div>
                    <div className="sidenav-user-info">
                        <div className="sidenav-user-name">
                            {user?.name}
                        </div>
                        <div className="sidenav-user-email">
                            {user?.email}
                        </div>
                    </div>
                </div>

                {/* Main Navigation */}
                <div className="sidenav-section">
                    <div className="sidenav-section-title">Main</div>
                    <ul className="sidenav-menu">
                        {navigationItems.map((item) => (
                            <li key={item.id} className="sidenav-menu-item">
                                <Link
                                    to={item.path}
                                    className={`sidenav-link ${item.active ? 'sidenav-link-active' : ''}`}
                                    onClick={() => window.innerWidth <= 768 && onToggle()}
                                >
                                    <i className={item.icon}></i>
                                    <span>{item.label}</span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Coming Soon Section */}
                <div className="sidenav-section">
                    <div className="sidenav-section-title">Coming Soon</div>
                    <ul className="sidenav-menu">
                        {comingSoonItems.map((item) => (
                            <li key={item.id} className="sidenav-menu-item">
                                <div className="sidenav-link sidenav-link-disabled">
                                    <i className={item.icon}></i>
                                    <span>{item.label}</span>
                                    <div className="sidenav-badge">Soon</div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Special Actions */}
                <div className="sidenav-section">
                    <div className="sidenav-section-title">Actions</div>
                    <ul className="sidenav-menu">
                        {specialItems.map((item) => (
                            <li key={item.id} className="sidenav-menu-item">
                                <Link
                                    to={item.path}
                                    className={`sidenav-link sidenav-link-special ${location.pathname === item.path ? 'sidenav-link-active' : ''}`}
                                    onClick={() => window.innerWidth <= 768 && onToggle()}
                                >
                                    <i className={item.icon}></i>
                                    <span>{item.label}</span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Admin Section - Only show for admin users */}
                {hasAnyAdminPermission && (
                    <div className="sidenav-section">
                        <div className="sidenav-section-title">
                            <i className="ri-admin-line"></i>
                            Admin Panel
                        </div>
                        <ul className="sidenav-menu">
                            {adminItems.map((item) => (
                                <li key={item.id} className="sidenav-menu-item">
                                    <Link
                                        to={item.path}
                                        className={`sidenav-link sidenav-link-admin ${item.active ? 'sidenav-link-active' : ''}`}
                                        onClick={() => window.innerWidth <= 768 && onToggle()}
                                    >
                                        <i className={item.icon}></i>
                                        <span>{item.label}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Footer Actions */}
                <div className="sidenav-footer">
                    <button
                        className="sidenav-logout-btn"
                        onClick={handleLogout}
                    >
                        <i className="ri-logout-box-line"></i>
                        <span>Logout</span>
                    </button>
                </div>
            </nav>
        </>
    );
};

export default SideNavigation;
